import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { StyleSheet, ActivityIndicator, View, Pressable, Platform, Linking } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { accountApi, whopApi } from '@/lib/api';
import { checkIsIapSubscriber, checkIsWhopSubscriber, checkIsValmarSubscriber, checkIsGoogleIapSubscriber, PROCESSOR, hasValidPlusAccess } from '@/lib/purchaseUtils';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import PricingUpgrade from '@/components/PricingUpgrade';
import BasicTable from '@/components/Table/BasicTable';
import BasicTableRow from '@/components/Table/BasicTableRow';
import BasicTableRowLoadMoreButton from '@/components/Table/BasicTableRowLoadMoreButton';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { presentCodeRedemptionSheetIOS } from 'react-native-iap';
import AutoTopUpSettings from '@/components/Settings/AutoTopUpSettings';
import moment from 'moment-mini';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useRestoreIapPurchases, { canRestoreIapPurchases } from '@/hooks/useRestoreIapPurchases';

const createStyles = (colors, isDark) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 24,
    position: 'relative',
  },
  centeredContainer: {
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    textAlign: 'center',
    padding: 20,
  },
  section: {
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    paddingBottom: 15,
  },
  sectionTitle: {
    marginBottom: 15,
  },
  planDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  planFeatures: {
    marginTop: 5,
  },
  feature: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 5,
    gap: 10,
  },
  featureIcon: {
    width: 25,
    height: 25,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  featureText: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
  },
  paymentMethod: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
  },
  paymentLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cardIcon: {
    width: 40,
    height: 24,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardDetails: {
    gap: 4,
  },
  nextBill: {
    marginTop: 16,
    padding: 12,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 8,
  },
  dangerZone: {
    width: 200,
    marginBottom: 25,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    overflow: 'hidden',
  },
  statusBadgeActive: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
  },
  statusBadgeCanceled: {
    backgroundColor: '#fee2e2',
    color: '#b91c1c',
  },
  statusBadgeExpired: {
    backgroundColor: isDark ? 'rgba(156, 163, 175, 0.2)' : '#f3f4f6',
    color: isDark ? '#9ca3af' : '#6b7280',
  },
  billingInfo: {
    marginTop: 12,
    gap: 8,
  },
  billingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bottom: {
    width: '100%',
  },
  // Status card styles
  statusCard: {
    padding: 16,
    borderRadius: 12,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    marginBottom: 16,
  },
  statusCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  statusCardContent: {
    gap: 8,
  },
  expirationWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#fef3c7',
    marginTop: 12,
  },
  // Payment history styles
  historySection: {
    borderRadius: 16,
    backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  historyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  historyHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  historyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  },
  autoTopUpBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.1)',
    marginLeft: 6,
  },
  emptyHistory: {
    padding: 40,
    alignItems: 'center',
  },
  receiptButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  upgradeSection: {
    marginTop: 16,
  },
  billingIssueBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  billingIssueBannerDark: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.25)',
  },
  billingIssueContent: {
    flex: 1,
    gap: 4,
  },
  billingIssueButton: {
    marginTop: 8,
  },
  iapWebNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 14,
    borderRadius: 10,
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    borderWidth: 1,
    borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  },
});

// Features data matching PricingModal
const buildFeatures = (isPlusPremium) => [
  {
    icon: 'infinity',
    title: 'Unlimited',
    description: 'messages with default AI model'
  },
  {
    icon: 'rocket',
    title: 'Supercharged limits',
    description: 'for conversations, custom agents, and collections'
  },
  {
    icon: 'sparkles',
    title: `${isPlusPremium ? PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY : PLUS_MONTHLY_CREDITS_DISPLAY} credits`,
    description: ['to use with', { text: 'other', bold: true }, 'AI models & tools']
  },
  {
    icon: 'bolt',
    title: 'Lightning-fast',
    description: ['responses with', { text: 'zero waiting time', bold: true }]
  }
];

const getProductId = (purchase) => {
  const product = purchase?.product?.id || purchase?.product;
  return typeof product === 'string' ? product : null;
};

const isPremiumPlan = (purchase) => {
  const product = getProductId(purchase);
  return Boolean(product && product.startsWith('plus-premium'));
};

const isAnnualPlan = (purchase) => {
  const product = getProductId(purchase);
  if (product === 'plus-premium-annual') return true;
  if (product === 'plus-premium-monthly') return false;
  if (purchase?.recurring?.schedule === 'yearly') return true;
  if (product === 'plus-annual') return true;
  if (purchase?.price >= 100) return true;
  return false;
};

const isWeeklyPlan = (purchase) => {
  if (purchase?.recurring?.schedule === 'weekly') return true;
  if (purchase?.productId?.includes?.('.weekly')) return true;
  if (purchase?.price && parseFloat(purchase.price) < 10 && parseFloat(purchase.price) >= 5) return true;
  return false;
};

const getPlanTypeLabel = (purchase) => {
  if (isAnnualPlan(purchase)) return 'Annual';
  if (isWeeklyPlan(purchase)) return 'Weekly';
  return 'Monthly';
};

const getBillingPeriod = (purchase) => {
  if (isAnnualPlan(purchase)) return 'year';
  if (isWeeklyPlan(purchase)) return 'week';
  return 'month';
};

// Format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const value = String(dateString);
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  };
  // Processor billing dates are date-only values persisted at UTC midnight.
  // Formatting those in a US timezone otherwise displays the previous day.
  if (/^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/.test(value)) {
    options.timeZone = 'UTC';
  }
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const formatShortDate = (dateString) => {
  if (!dateString) return '—';
  const date = moment(dateString);
  const currentYear = moment().year();
  const dateYear = date.year();
  
  if (dateYear === currentYear) {
    return date.format('MMM D');
  }
  
  return date.format('MMM D, YYYY');
};

const formatAmount = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

export default function SubscriptionSettings() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const userActivePurchase = useAuthStore(state => state.user?.activePurchase);
  const billingIssue = useAuthStore(state => state.user?.activePurchase?.billingIssue);
  const hasWhopSubscription = useAuthStore(state => state.user?.whop?.hasSubscription);
  const plusExpiresAt = useAuthStore(state => state.user?.plusExpiresAt);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isComped = useAuthStore(state => state.user?.flags?.isComped);
  const setActivePurchase = useAuthStore(state => state.setActivePurchase);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const hasValmarPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const userValmarCard = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { openUpdatePaymentMethod, isInternational } = useGabPurchase();
  const { t } = useTranslation();
  const { isRestoring: isRestoringPurchases, restorePurchases } = useRestoreIapPurchases();
  const showRestorePurchases = canRestoreIapPurchases();
  // Always verify billing data before rendering it. Persisted purchase state is
  // useful as a fallback, but should not flash stale plan details on first paint.
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(true);
  const [upgradeCtaLabel, setUpgradeCtaLabel] = useState(t('settings.subscription.upgradeNow'));
  const handleUpgradeSelectionChange = useCallback((selection) => {
    if (selection?.ctaLabel) setUpgradeCtaLabel(selection.ctaLabel);
  }, []);

  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(userActivePurchase),
    [userActivePurchase],
  );

  const isGoogleIap = useMemo(
    () => checkIsGoogleIapSubscriber(userActivePurchase),
    [userActivePurchase],
  );

  const isWhopSubscriber = useMemo(
    () => checkIsWhopSubscriber(
      userActivePurchase,
      Boolean(isInternational && !isPlus),
      Boolean(hasWhopSubscription),
    ),
    [userActivePurchase, isInternational, isPlus, hasWhopSubscription],
  );

  const hasValidPlus = useMemo(
    () => hasValidPlusAccess({
      isPlus,
      isComped,
      plusExpiresAt,
      purchase: userActivePurchase,
    }),
    [isPlus, isComped, plusExpiresAt, userActivePurchase],
  );

  const plusAccessExpired = Boolean(
    plusExpiresAt && new Date(plusExpiresAt) <= new Date() && !hasValidPlus,
  );

  const pricingRef = useRef();

  // Payment history state
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({});
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);

  useEffect(() => {
    // Always refresh subscription data from the backend when the billing tab mounts.
    // This ensures changes made outside the app (e.g. WHOP cancel/re-subscribe) are reflected.
    fetchSubscription();
    // Also refresh the full user record so `state.user.plusExpiresAt` reflects
    // any backend-side changes (admin manual extensions, Whop renewals, etc.).
    // Without this, the "Next Billing Date" can be stuck on the cached value
    // from when the user logged in, even though the database is correct.
    fetchUser?.()?.catch((err) => console.error('Failed to refresh user on billing tab mount', err));
  }, []);

  useEffect(() => {
    // Fetch purchase history on mount
    fetchPurchaseHistory(1);
  }, []);

  const fetchSubscription = async () => {
    try {
      setIsLoadingPurchase(true);
      const purchase = await accountApi.getActivePurchase();
      // A valid cached purchase is better than the API's null fallback for a
      // user who still has current Plus access. Do not keep a stale row when
      // Plus has already expired.
      if (purchase || !hasValidPlus) {
        setActivePurchase(purchase);
      }
      setIsLoadingPurchase(false);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      addToast(t('settings.subscription.fetchFailed'), 'error');
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  const fetchPurchaseHistory = async (page = 1) => {
    try {
      if (page === 1) {
        setIsLoadingHistory(true);
      } else {
        setIsLoadingMoreHistory(true);
      }
      
      const { purchases, pagination } = await accountApi.getPurchaseHistory(page);
      
      if (page === 1) {
        setPurchaseHistory(purchases);
      } else {
        setPurchaseHistory(prev => [...prev, ...purchases]);
      }
      setHistoryPagination(pagination);
    } catch (err) {
      console.error('Error fetching purchase history:', err);
      // Don't show toast for empty history
      if (err.status !== 404) {
        addToast(t('settings.subscription.historyFetchFailed'), 'error');
      }
    } finally {
      setIsLoadingHistory(false);
      setIsLoadingMoreHistory(false);
    }
  };

  const handleLoadMoreHistory = useCallback(async () => {
    if (isLoadingMoreHistory || !historyPagination?.hasMore) return;
    const nextPage = (historyPagination?.current || historyPagination?.page || 1) + 1;
    await fetchPurchaseHistory(nextPage);
  }, [isLoadingMoreHistory, historyPagination]);

  const handleDownloadReceipt = useCallback(async (purchaseId, purchaseData = {}) => {
    try {
      setDownloadingReceiptId(purchaseId);
      
      // Fetch receipt from API (supports both subscription and credit purchases)
      const receipt = await accountApi.getReceipt(purchaseId);
      
      if (receipt) {
        if (Platform.OS === 'web') {
          // Generate HTML receipt from JSON data
          const isCreditPurchase = receipt.type === 'credits';
          const productName = isCreditPurchase
            ? `${receipt.product}${receipt.isAutoTopup ? ' (Auto Top-Up)' : ''}`
            : (receipt.productName
              || (typeof receipt.product === 'string' && receipt.product.includes('premium') ? 'Gab AI Plus Premium' : null)
              || 'Gab AI Plus');
          const status = receipt.status || 'completed';
          const statusColors = {
            completed: { bg: '#dcfce7', text: '#15803d' },
            success: { bg: '#dcfce7', text: '#15803d' },
            active: { bg: '#dcfce7', text: '#15803d' },
            pending: { bg: '#fef3c7', text: '#92400e' },
            refunded: { bg: '#fee2e2', text: '#b91c1c' },
            failed: { bg: '#fee2e2', text: '#b91c1c' },
          };
          const statusStyle = statusColors[status.toLowerCase()] || statusColors.completed;
          const formattedDate = receipt.paymentReceivedDate || receipt.createdAt 
            ? new Date(receipt.paymentReceivedDate || receipt.createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', month: 'long', day: 'numeric' 
              })
            : 'N/A';
          
          const receiptHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Receipt - Gab AI</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f8fafc; padding: 40px 20px; min-height: 100vh; }
    .receipt { max-width: 560px; margin: 0 auto; background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.08); }
    .header { background: linear-gradient(135deg, #3696F5 0%, #495aff 100%); color: white; padding: 32px; text-align: center; }
    .logo { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 8px; }
    .logo svg { width: 32px; height: 32px; }
    .logo h1 { font-size: 26px; font-weight: 700; letter-spacing: -0.5px; }
    .header p { opacity: 0.9; font-size: 14px; font-weight: 500; }
    .content { padding: 32px; }
    .status-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
    .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 20px; font-size: 13px; font-weight: 600; text-transform: capitalize; background: ${statusStyle.bg}; color: ${statusStyle.text}; }
    .status-badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
    .receipt-number { font-size: 13px; color: #6b7280; }
    .details { background: #f8fafc; border-radius: 12px; overflow: hidden; margin-bottom: 24px; }
    .detail-row { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e5e7eb; }
    .detail-row:last-child { border-bottom: none; }
    .detail-row .label { color: #6b7280; font-size: 14px; }
    .detail-row .value { color: #111827; font-size: 14px; font-weight: 600; text-align: right; max-width: 60%; word-break: break-all; }
    .total { background: linear-gradient(135deg, #3696F5 0%, #495aff 100%); padding: 24px; border-radius: 12px; text-align: center; margin-bottom: 24px; color: white; }
    .total .label { font-size: 13px; opacity: 0.9; margin-bottom: 4px; font-weight: 500; }
    .total .amount { font-size: 36px; font-weight: 700; letter-spacing: -1px; }
    .thank-you { text-align: center; color: #374151; font-size: 15px; font-weight: 500; margin-bottom: 8px; }
    .tagline { text-align: center; color: #9ca3af; font-size: 13px; }
    .footer { border-top: 1px solid #e5e7eb; padding: 24px 32px; background: #f8fafc; }
    .footer-content { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 20px; }
    .footer-left { font-size: 12px; color: #6b7280; line-height: 1.7; }
    .footer-left strong { color: #374151; font-size: 13px; }
    .footer-right { text-align: right; font-size: 12px; color: #9ca3af; }
    .footer-right a { color: #3696F5; text-decoration: none; }
    .footer-badges { display: flex; gap: 12px; margin-top: 8px; justify-content: flex-end; }
    .footer-badge { font-size: 11px; color: #6b7280; }
    @media print { 
      body { background: white; padding: 0; } 
      .receipt { box-shadow: none; border: 1px solid #e5e7eb; } 
    }
    @media (max-width: 480px) {
      .footer-content { flex-direction: column; }
      .footer-right { text-align: left; }
      .footer-badges { justify-content: flex-start; }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <div class="logo">
        <svg viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
        <h1>Gab AI</h1>
      </div>
      <p>Payment Receipt</p>
    </div>
    <div class="content">
      <div class="status-row">
        <div class="status-badge">${status}</div>
        <span class="receipt-number">#${(receipt.transactionId || receipt.id || purchaseId).toString().slice(-8).toUpperCase()}</span>
      </div>
      <div class="details">
        <div class="detail-row">
          <span class="label">Transaction ID</span>
          <span class="value">${receipt.transactionId || receipt.id || 'N/A'}</span>
        </div>
        <div class="detail-row">
          <span class="label">Date</span>
          <span class="value">${formattedDate}</span>
        </div>
        <div class="detail-row">
          <span class="label">Product</span>
          <span class="value">${productName}</span>
        </div>
        ${isCreditPurchase && receipt.credits ? `<div class="detail-row">
          <span class="label">Credits</span>
          <span class="value">${receipt.credits.toLocaleString()} credits</span>
        </div>` : ''}
        <div class="detail-row">
          <span class="label">Email</span>
          <span class="value">${receipt.userEmail || 'N/A'}</span>
        </div>
      </div>
      <div class="total">
        <div class="label">Total Paid</div>
        <div class="amount">${formatAmount(receipt.price, receipt.currency)}</div>
      </div>
      <p class="thank-you">Thank you for your purchase!</p>
      <p class="tagline">The AI platform for free thinkers</p>
    </div>
    <div class="footer">
      <div class="footer-content">
        <div class="footer-left">
          <strong>Gab AI Inc.</strong><br>
          1143 Northern Blvd #158<br>
          Clarks Summit, PA 18411<br>
          United States of America
        </div>
        <div class="footer-right">
          <a href="mailto:ai@gab.com">ai@gab.com</a><br>
          <a href="https://gab.ai">gab.ai</a>
          <div class="footer-badges">
            <span class="footer-badge">Christ is King ✝️</span>
            <span class="footer-badge">Made in USA 🇺🇸</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;

          const blob = new Blob([receiptHtml], { type: 'text/html' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `gab-ai-receipt-${receipt.transactionId || receipt.id || purchaseId}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // For native, use expo-file-system and expo-sharing
          try {
            // Generate a simpler text-based receipt for native
            const isCreditPurchase = receipt.type === 'credits';
            const productName = isCreditPurchase 
              ? `${receipt.product}${receipt.isAutoTopup ? ' (Auto Top-Up)' : ''}`
              : (Platform.OS === 'macos'
                ? (receipt.productName || (receipt.product?.includes('premium') ? 'Gab AI Plus Premium' : 'Gab AI Plus'))
                : (receipt.product || 'Gab AI Plus'));
            const receiptAmount = Platform.OS === 'macos'
              ? formatAmount(receipt.price, receipt.currency) : `$${receipt.amount || '0.00'}`;
            const receiptEmail = Platform.OS === 'macos' ? receipt.userEmail : receipt.email;
            const formattedDate = receipt.paymentReceivedDate || receipt.createdAt 
              ? new Date(receipt.paymentReceivedDate || receipt.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })
              : 'N/A';
            
            const receiptText = `
GAB AI RECEIPT
==============

Product: ${productName}
Date: ${formattedDate}
Status: ${receipt.status || 'Completed'}
Amount: ${receiptAmount}

Transaction ID: ${receipt.transactionId || receipt.id || purchaseId}
${receiptEmail ? `Email: ${receiptEmail}` : ''}
${receipt.paymentMethod ? `Payment Method: ${receipt.paymentMethod}` : ''}

Thank you for your purchase!
Visit us at gab.ai

This receipt was generated by Gab AI.
            `.trim();
            
            const filename = `gab-ai-receipt-${receipt.transactionId || receipt.id || purchaseId}.txt`;
            const fileUri = `${FileSystem.documentDirectory}${filename}`;
            
            await FileSystem.writeAsStringAsync(fileUri, receiptText, {
              encoding: FileSystem.EncodingType.UTF8,
            });
            
            const canShare = await Sharing.isAvailableAsync();
            if (canShare) {
              await Sharing.shareAsync(fileUri, {
                mimeType: 'text/plain',
                dialogTitle: 'Save Receipt',
              });
            } else {
              addToast(t('settings.subscription.sharingNotAvailable'), 'error');
            }
          } catch (nativeErr) {
            console.error('Error saving receipt on native:', nativeErr);
            addToast(t('settings.subscription.failedToSaveReceipt'), 'error');
          }
        }
      } else {
        addToast(t('settings.subscription.receiptNotAvailable'), 'error');
      }
    } catch (err) {
      console.error('Error downloading receipt:', err);
      addToast(t('settings.subscription.failedToDownloadReceipt'), 'error');
    } finally {
      setDownloadingReceiptId(null);
    }
  }, [addToast]);

  const applyCanceledStateLocally = (result) => {
    const accessEndDate = result?.accessUntil
      || result?.expiresAt
      || result?.recurring?.endDate
      || userActivePurchase?.recurring?.endDate
      || plusExpiresAt;

    const basePurchase = userActivePurchase || {
      id: result?.purchaseId,
      status: 'active',
      recurring: { enabled: true },
    };

    setActivePurchase({
      ...basePurchase,
      canceledDate: result?.canceledDate || new Date(),
      recurring: {
        ...(basePurchase.recurring || {}),
        enabled: false,
        endDate: accessEndDate,
      },
    });
  };

  const handleCancelWhopSubscription = useCallback(() => {
    openModal('ConfirmationModal', {
      title: t('settings.cancelPurchase.title'),
      message: t('settings.cancelPurchase.warningMessage'),
      confirmText: t('settings.cancelPurchase.yesCancel'),
      cancelText: t('settings.cancelPurchase.neverMind'),
      isDanger: true,
      onConfirm: async () => {
        try {
          setIsLoading(true);
          const result = await whopApi.cancelWhopSubscription();
          applyCanceledStateLocally(result);
          addToast(t('settings.subscription.canceledSuccess'), 'success');
          closeModal();
        } catch (error) {
          console.error('Error canceling Whop subscription:', error);
          addToast(error.message || t('settings.cancelPurchase.failedToCancel'), 'error');
        } finally {
          setIsLoading(false);
        }
      },
    });
  }, [openModal, closeModal, addToast, t, userActivePurchase, plusExpiresAt, setActivePurchase]);

  // The existing password-confirmation cancel modal. Extracted so the
  // cancellation-save flow can chain to it when the user opts to cancel anyway.
  const openCancelPurchaseModal = useCallback(() => {
    openModal('CancelPurchaseModal', {
      onConfirm: async (password, reason) => {
        try {
          setIsLoading(true);
          const result = await accountApi.cancelPlan(password, reason);

          // Get the access end date from various possible API response fields
          const accessEndDate = result.accessUntil || result.expiresAt || result.recurring?.endDate || userActivePurchase?.recurring?.endDate || plusExpiresAt;

          // activePurchase can be null for legacy Plus users whose Purchase row
          // hasn't loaded or doesn't match active-purchase query criteria.
          const basePurchase = userActivePurchase || {
            id: result.purchaseId,
            status: 'active',
            recurring: { enabled: true },
          };

          setActivePurchase({
            ...basePurchase,
            canceledDate: result.canceledDate,
            recurring: {
              ...(basePurchase.recurring || {}),
              enabled: false,
              endDate: accessEndDate,
            },
          });

          addToast(t('settings.subscription.canceledSuccess'), 'success');
          closeModal();
        } catch (error) {
          const partial = error?.data;
          if (partial?.code === 'SUBSCRIPTION_CANCELLATION_PARTIAL'
            && userActivePurchase?.id
            && partial.canceledPurchaseIds?.includes(userActivePurchase.id)) {
            setActivePurchase({
              ...userActivePurchase,
              canceledDate: partial.canceledDate,
              recurring: {
                ...(userActivePurchase.recurring || {}),
                enabled: false,
                endDate: partial.accessUntil || userActivePurchase.recurring?.endDate || plusExpiresAt,
              },
            });
            error.message = 'Your current plan was canceled. Another subscription could not be confirmed canceled and may still renew. Please retry to cancel the remaining subscriptions or contact support.';
          }
          throw error; // Let the modal handle the error display
        } finally {
          setIsLoading(false);
        }
      },
      onClose: () => {
        closeModal();
        openModal('SettingsModalWeb', { tab: 'subscription' });
      },
    });
  }, [openModal, closeModal, setActivePurchase, userActivePurchase, plusExpiresAt, addToast, t]);

  const handleCancelSubscription = async () => {
    // Whop subscribers cancel via our backend (Whop's hosted portal sometimes
    // only exposes Extend/Upgrade and hides the cancel action).
    if (isWhopSubscriber) {
      handleCancelWhopSubscription();
      return;
    }

    // Web + Valmar subscribers see the cancellation-save flow first (a retention
    // offer). Every other path (native IAP, legacy processors) and any decline
    // falls through to the plain password-confirmation cancel modal.
    // See docs/PLUS_CANCELLATION_SAVE.md Part II/§30.
    if (Platform.OS === 'web' && isValmarSubscriber) {
      openModal('CancellationSaveModal', {
        onAccepted: () => {
          addToast(t('modals.cancellationSave.offerApplied'), 'success');
          closeModal();
          openModal('SettingsModalWeb', { tab: 'subscription' });
        },
        onCancelAnyway: () => {
          // Replace the save modal with the real cancel modal.
          closeModal();
          openCancelPurchaseModal();
        },
        onClose: () => {
          closeModal();
          openModal('SettingsModalWeb', { tab: 'subscription' });
        },
      });
      return;
    }

    openCancelPurchaseModal();
  };

  // Handle manage subscription for international Whop users
  const [isLoadingManage, setIsLoadingManage] = useState(false);
  
  const handleManageSubscription = useCallback(async () => {
    try {
      setIsLoadingManage(true);
      const response = await whopApi.getBillingPortalUrl();
      const portalUrl = response.portalUrl || response.manageUrl;
      
      if (!portalUrl) {
        throw new Error('Failed to get Whop billing portal URL');
      }
      
      // Open Whop billing portal
      if (Platform.OS === 'web') {
        window.open(portalUrl, '_blank');
      } else {
        await Linking.openURL(portalUrl);
      }
    } catch (error) {
      console.error('Error opening Whop billing portal:', error);
      addToast(error.message || t('settings.subscription.failedToOpenPortal'), 'error');
    } finally {
      setIsLoadingManage(false);
    }
  }, [addToast]);

  const handleUpdatePaymentMethod = useCallback(async () => {
    try {
      setIsUpdatingPayment(true);
      
      // Whop subscribers update payment methods via Whop billing portal
      if (isWhopSubscriber) {
        const response = await whopApi.getBillingPortalUrl();
        const portalUrl = response.portalUrl || response.manageUrl;
        
        if (!portalUrl) {
          throw new Error('Failed to get Whop billing portal URL');
        }
        
        // Open Whop billing portal
        if (Platform.OS === 'web') {
          window.open(portalUrl, '_blank');
        } else {
          await Linking.openURL(portalUrl);
        }
        return;
      }
      
      // Web and macOS use the existing hosted Valmar payment-method form.
      if (Platform.OS !== 'web' && Platform.OS !== 'macos') {
        addToast(t('settings.subscription.manageWebPayment'), 'info');
        return;
      }
      
      const response = await accountApi.initiatePaymentMethodUpdate();
      
      if (!response.updateUrl) {
        throw new Error('Failed to get update URL');
      }
      
      // Open the update payment iframe with the URL from the API
      const supportedPlanLengths = ['monthly', 'yearly', 'premium-monthly', 'premium-yearly'];
      await openUpdatePaymentMethod(response.updateUrl, {
        planLength: supportedPlanLengths.includes(response.planLength)
          ? response.planLength
          : 'monthly',
      });
    } catch (error) {
      console.error('Error updating payment method:', error);
      if (error.message !== 'User closed checkout' && 
          error.message !== 'Backdrop close' && 
          error.message !== 'User closed update payment') {
        addToast(error.message || t('settings.subscription.failedToUpdatePayment'), 'error');
      }
    } finally {
      setIsUpdatingPayment(false);
    }
  }, [openUpdatePaymentMethod, addToast, isWhopSubscriber]);

  // Table columns for payment history
  const tableColumns = useMemo(() => [
    {
      id: 'date',
      header: t('settings.subscription.date'),
      width: 100,
      headerStyle: { paddingLeft: 6 },
      cellStyle: { paddingLeft: 6 },
    },
    {
      id: 'description',
      header: t('settings.subscription.description'),
      width: Platform.OS === 'macos' ? 'auto' : 'calc(100% - 280px)',
      headerStyle: { paddingLeft: 8 },
      cellStyle: { paddingLeft: 8 },
    },
    {
      id: 'amount',
      header: t('settings.subscription.amount'),
      width: 80,
      headerStyle: { paddingLeft: 8 },
      cellStyle: { paddingLeft: 8 },
    },
    {
      id: 'status',
      header: t('settings.subscription.status'),
      width: 70,
      headerStyle: { paddingLeft: 8 },
      cellStyle: { paddingLeft: 8 },
    },
    {
      id: 'receipt',
      header: '',
      width: 30,
      headerStyle: { paddingLeft: 8 },
      cellStyle: { paddingLeft: 8, alignItems: 'flex-end' },
    },
  ], [t]);

  // Render column content for payment history
  const renderHistoryColumn = useCallback((column, row) => {
    // Detect if this is a credit pack purchase
    const isCreditPurchase = row.type === 'credits' || row.creditPackId || row.credits;
    
    switch (column.id) {
      case 'date':
        return (
          <Text size="s" color="primary">
            {formatShortDate(row.createdAt || row.date)}
          </Text>
        );
      case 'description':
        if (isCreditPurchase) {
          const credits = row.credits || row.creditPack?.credits;
          return (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <Text size="s" color="primary" numberOfLines={1}>
                {credits ? `${credits.toLocaleString()} Credits` : t('settings.subscription.creditPack')}
              </Text>
              {row.isAutoTopup && (
                <View style={styles.autoTopUpBadge}>
                  <Text size="xxs" weight="medium" style={{ color: '#8B5CF6' }}>
                    {t('settings.subscription.autoTopUp')}
                  </Text>
                </View>
              )}
            </View>
          );
        }
        const planType = getPlanTypeLabel(row);
        return (
          <Text size="s" color="primary" numberOfLines={1}>
            {row.product?.name || (isPremiumPlan(row) ? 'Gab AI Plus Premium' : 'Gab AI Plus')} {planType}
          </Text>
        );
      case 'amount':
        return (
          <Text size="s" color="primary">
            {formatAmount(row.price || row.amount, row.currency)}
          </Text>
        );
      case 'status':
        const status = row.status || 'completed';
        const statusColor = status === 'active' || status === 'completed' || status === 'success' ? 'success' : 
                          status === 'canceled' || status === 'failed' ? 'danger' : 'secondary';
        return (
          <Text size="xs" color={statusColor} style={{ textTransform: 'capitalize' }}>
            {status === 'active' || status === 'success' ? t('settings.subscription.paid') : status}
          </Text>
        );
      case 'receipt':
        const isDownloading = downloadingReceiptId === row.id;
        return (
          <Pressable
            onPress={() => handleDownloadReceipt(row.id, row)}
            disabled={isDownloading}
            style={({ hovered }) => [
              styles.receiptButton,
              hovered && { backgroundColor: colors.solid_color_tertiary }
            ]}
          >
            {isDownloading ? (
              <ActivityIndicator size="small" color={colors.text_color_secondary} />
            ) : (
              <Icon name="download" size={12} color={colors.text_color_secondary} />
            )}
          </Pressable>
        );
      default:
        return null;
    }
  }, [downloadingReceiptId, handleDownloadReceipt, styles.receiptButton, colors]);

  // Present Apple's native offer code redemption sheet (iOS only)
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);
  const handleRedeemCode = useCallback(async () => {
    if (Platform.OS !== 'ios') return;
    try {
      setIsRedeemingCode(true);
      await presentCodeRedemptionSheetIOS();
    } catch (error) {
      console.error('Error presenting code redemption sheet:', error);
    } finally {
      setIsRedeemingCode(false);
    }
  }, []);

  // For Plus users whose Purchase row is missing or drifted off the
  // active-purchase response (legacy Paddle, admin/manual Plus, or stale
  // processor status),
  // synthesize a minimal purchase object so the active-subscription
  // view renders instead of the "No Active Subscription" empty state
  // and the upgrade-from-empty-state flow that rejects Plus users.
  // Never synthesize an Active card after plusExpiresAt — that produced
  // "Active" with Started/Expires N/A for lapsed Paddle annuals.
  const purchase = userActivePurchase || (hasValidPlus ? {
    status: 'active',
    // The access flags identify the tier, not its billing cadence. Do not
    // invent an annual SKU when the Purchase row is unavailable.
    product: null,
    price: null,
    currency: 'USD',
    paymentReceivedDate: null,
    canceledDate: null,
    // Prefer Whop when we know the user has an active Whop membership, then
    // Valmar (card on file). This keeps payment management visible even when
    // the Purchase row hasn't loaded or drifted off the active-purchase response.
    processor: isWhopSubscriber
      ? PROCESSOR.WHOP
      : (hasValmarPaymentMethod ? PROCESSOR.VALMAR : null),
    // A saved Valmar card does not establish recurring billing.
    recurring: { enabled: Boolean(isWhopSubscriber) },
  } : null);

  const handleManageStoreSubscription = useCallback(async () => {
    const url = purchase?.processor === PROCESSOR.GOOGLE
      ? 'https://play.google.com/store/account/subscriptions'
      : purchase?.processor === PROCESSOR.APPLE
        ? 'https://apps.apple.com/account/subscriptions' : null;
    if (!url) return;
    try {
      await Linking.openURL(url);
    } catch (error) {
      useToastStore.getState().actions.addToast(error?.message || 'Unable to open subscription management.', 'error');
    }
  }, [purchase?.processor]);

  if (isLoadingPurchase) {
    return (
      <View style={styles.centeredContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  const handleUpgrade = async () => {
    // Current Plus with no purchase row (legacy/admin) still goes through
    // extend. Lapsed Plus must use new-subscriber checkout so they can pick
    // regular Plus rather than being forced into Plus Premium.
    if (hasValidPlus) {
      openModal('SubscriptionExtendModal');
      return;
    }
    if (pricingRef.current) {
      await pricingRef.current.handleUpgrade();
    }
  };

  const isValmarSubscriber = checkIsValmarSubscriber(purchase, { hasValmarPaymentMethod, isPlus: hasValidPlus });
  const isActive = hasValidPlus && purchase?.status === 'active';
  const isCanceled = Boolean(purchase?.canceledDate);
  const hasActiveSubscription = isActive && purchase;
  const hasProcessorSubscription = Boolean(
    purchase?.processorSubscriptionId ||
    purchase?.recurring?.subscriptionId
  );
  const isDirectlyCancelableProcessor = [
    PROCESSOR.VALMAR,
    PROCESSOR.AUTHNET,
    PROCESSOR.PADDLE,
  ].includes(purchase?.processor);
  const hasCancelableSubscription = Boolean(
    hasActiveSubscription &&
    !isCanceled &&
    (purchase?.processor !== PROCESSOR.VALMAR ||
      (purchase?.recurring?.enabled === true && hasProcessorSubscription)) &&
    (
      isIapSubscriber ||
      isWhopSubscriber ||
      isDirectlyCancelableProcessor ||
      (isPlus && hasValmarPaymentMethod && !isIapSubscriber && !isWhopSubscriber)
    )
  );
  const isRecurring = !isCanceled && (purchase?.processor === PROCESSOR.VALMAR
    ? purchase?.recurring?.enabled === true && hasProcessorSubscription
    : purchase?.recurring?.enabled ||
      (purchase?.recurring?.enabled !== false && hasProcessorSubscription) ||
      (isActive && purchase?.recurring?.enabled !== false && purchase?.processor === PROCESSOR.WHOP));
  const hasKnownPlanCadence = Boolean(
    getProductId(purchase)
    || purchase?.recurring?.schedule
    || purchase?.price,
  );

  // Get the subscription start date from various possible fields
  const getStartDate = () => {
    // Try recurring.startDate first
    if (purchase?.recurring?.startDate) {
      return formatDate(purchase.recurring.startDate);
    }
    // Fall back to paymentReceivedDate (when payment was first received)
    if (purchase?.paymentReceivedDate) {
      return formatDate(purchase.paymentReceivedDate);
    }
    // Fall back to createdAt (when purchase record was created)
    if (purchase?.createdAt) {
      return formatDate(purchase.createdAt);
    }
    return 'N/A';
  };

  // Get the raw start date for calculations
  const getRawStartDate = () => {
    return purchase?.recurring?.startDate || purchase?.paymentReceivedDate || purchase?.createdAt;
  };

  // For canceled subscriptions, calculate access end date if not provided
  // Monthly subscriptions renew on the same date each month from start date
  const getAccessEndDate = () => {
    // Non-renewing access has a fixed expiration. Never advance a purchase
    // anniversary into the future as though another cycle had been paid.
    if (!isRecurring && !isCanceled) {
      const expiresAt = [plusExpiresAt, purchase?.recurring?.endDate]
        .find(date => date && Number.isFinite(new Date(date).getTime()));
      return expiresAt ? formatDate(expiresAt) : 'N/A';
    }
    // Use the backend-provided plusExpiresAt for active subscriptions when it's
    // still in the future — it correctly accounts for extended/carried-over days.
    // If plusExpiresAt is stale (renewal day before webhook fires), fall through
    // to purchase-based dates so the UI doesn't show a past date while "Active".
    if (plusExpiresAt && !isCanceled && new Date(plusExpiresAt) > new Date()) {
      return formatDate(plusExpiresAt);
    }

    // Fall back to the explicit end date from recurring data
    if (purchase?.recurring?.endDate) {
      return formatDate(purchase.recurring.endDate);
    }
    
    const rawStartDate = getRawStartDate();
    const isAnnual = isAnnualPlan(purchase);
    const isWeekly = isWeeklyPlan(purchase);
    
    const renewalUnit = isAnnual ? 'year' : isWeekly ? 'week' : 'month';

    // For canceled subscriptions without end date, try to calculate from start date
    if (isCanceled && rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now)) {
        nextDate.add(1, renewalUnit);
      }
      return formatDate(nextDate.toISOString());
    }
    
    // For active subscriptions without explicit end date, calculate from start date
    if (isAnnual && rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
        nextDate.add(1, 'year');
      }
      return formatDate(nextDate.toISOString());
    }
    
    if (rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
        nextDate.add(1, renewalUnit);
      }
      return formatDate(nextDate.toISOString());
    }
    
    return 'N/A';
  };

  const getNextRenewalDate = () => {
    // The account expiration includes time carried over by an extension,
    // even when an older Purchase still has its original renewal dates.
    // Apple/Google billing dates remain store-controlled: an account access
    // grant does not move the next charge in those stores.
    if (!isIapSubscriber && !isCanceled && plusExpiresAt && new Date(plusExpiresAt) > new Date()) {
      return formatDate(plusExpiresAt);
    }

    const storedNext = [purchase?.recurring?.nextBilledAt, purchase?.recurring?.endDate]
      .find(date => date && new Date(date) > new Date());
    if (storedNext) {
      return formatDate(storedNext);
    }

    const rawStartDate = getRawStartDate();
    if (!rawStartDate) return 'N/A';

    const renewalUnit = isAnnualPlan(purchase)
      ? 'year'
      : isWeeklyPlan(purchase)
        ? 'week'
        : 'month';
    const nextDate = moment(rawStartDate);
    const now = moment();
    while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
      nextDate.add(1, renewalUnit);
    }
    return formatDate(nextDate.toISOString());
  };
  
  const nextBillDate = isRecurring && !isCanceled ? getNextRenewalDate() : getAccessEndDate();
  const startDate = getStartDate();
  const expiredOnDate = plusAccessExpired ? formatDate(plusExpiresAt) : null;

  // Render subscription status section
  const renderSubscriptionStatus = () => {
    if (!hasActiveSubscription) {
      const expiredHeader = plusAccessExpired ? (
        <>
          <View style={styles.statusCardHeader}>
            <Text size="l" weight="bold">
              {isPlusPremium ? 'Gab AI Plus Premium' : 'Gab AI Plus'}
            </Text>
            <View style={[styles.statusBadge, styles.statusBadgeExpired]}>
              <Text size="s" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
                {t('settings.subscription.expired')}
              </Text>
            </View>
          </View>
          <View style={styles.statusCardContent}>
            {startDate !== 'N/A' ? (
              <View style={styles.billingRow}>
                <Text size="n" color="secondary">{t('settings.subscription.started')}</Text>
                <Text size="n" color="primary">{startDate}</Text>
              </View>
            ) : null}
            <View style={styles.billingRow}>
              <Text size="n" color="secondary">{t('settings.subscription.expired')}</Text>
              <Text size="n" color="primary">{expiredOnDate}</Text>
            </View>
          </View>
          <Text size="n" color="secondary" style={{ marginTop: 8 }}>
            {t('settings.subscription.subscriptionExpiredMsg')}
          </Text>
        </>
      ) : (
        <>
          <View style={styles.statusCardHeader}>
            <Text size="l" weight="bold">{t('settings.subscription.noActive')}</Text>
            <View style={[styles.statusBadge, styles.statusBadgeExpired]}>
              <Text size="s" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>{t('settings.subscription.inactive')}</Text>
            </View>
          </View>
          <Text size="n" color="secondary">
            {t('settings.subscription.upgradeDescription')}
          </Text>
        </>
      );

      if (Platform.OS === 'ios' || Platform.OS === 'android') {
        return (
          <View style={styles.statusCard}>
            {expiredHeader}
            <View style={{ marginTop: 16, gap: 8 }}>
              <Button
                size='large'
                text={plusAccessExpired ? t('settings.subscription.resubscribe') : t('settings.subscription.viewPlans')}
                textWeight='bold'
                onPress={() => router.push('/paywall')}
                variant='plus'
                prependIcon="sparkles"
              />
              {showRestorePurchases && (
                <Button
                  variant="secondary"
                  text={isRestoringPurchases ? t('settingsMobile.restoringPurchases') : t('settings.subscription.restorePurchases')}
                  onPress={restorePurchases}
                  isLoading={isRestoringPurchases}
                  prependIcon="rotate-right"
                  isFullWidth
                />
              )}
            </View>
          </View>
        );
      }
      return (
        <View style={styles.statusCard}>
          {expiredHeader}
          <View style={styles.upgradeSection}>
            <PricingUpgrade 
              ref={pricingRef}
              withPadding={false}
              withScroll={false}
              title=""
              subtitle=""
              onSelectionChange={handleUpgradeSelectionChange}
            />
            <View style={{ marginTop: 16 }}>
              <Button
                size='large'
                text={upgradeCtaLabel}
                textWeight='bold'
                onPress={handleUpgrade}
                variant='plus'
                isLoading={pricingRef.current?.isLoading}
                disabled={pricingRef.current?.isLoading}
              />
            </View>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.statusCard}>
        <View style={styles.statusCardHeader}>
          <View>
            <Text size="l" weight="bold" color="primary">
              {isPlusPremium
                ? 'Gab AI Plus Premium'
                : (purchase.product?.name || 'Gab AI Plus')}
              {hasKnownPlanCadence ? ` ${getPlanTypeLabel(purchase)}` : ''}
            </Text>
          </View>
          <View style={[
            styles.statusBadge,
            isActive ? styles.statusBadgeActive : styles.statusBadgeCanceled
          ]}>
            <Text size="s" style={{ color: isActive ? '#15803d' : '#b91c1c' }}>
              {isCanceled ? t('settings.subscription.canceled') : t('settings.subscription.active')}
            </Text>
          </View>
        </View>

        <View style={styles.statusCardContent}>
          <View style={styles.billingRow}>
            <Text size="n" color="secondary">{t('settings.subscription.started')}</Text>
            <Text size="n" color="primary">{startDate}</Text>
          </View>
          
          {isCanceled ? (
            <View style={styles.billingRow}>
              <Text size="n" color="secondary">{t('settings.subscription.accessUntil')}</Text>
              <Text size="n" color="danger" weight="bold">{nextBillDate}</Text>
            </View>
          ) : isRecurring ? (
            <View style={styles.billingRow}>
              <Text size="n" color="secondary">{t('settings.subscription.nextBillingDate')}</Text>
              <Text size="n" color="primary">{nextBillDate}</Text>
            </View>
          ) : (
            <View style={styles.billingRow}>
              <Text size="n" color="secondary">{t('settings.subscription.expires')}</Text>
              <Text size="n" color="primary">{nextBillDate}</Text>
            </View>
          )}

          {purchase.price && (
            <View style={styles.billingRow}>
              <Text size="n" color="secondary">{t('settings.subscription.amount')}</Text>
              <Text size="n" color="primary">{formatAmount(purchase.price, purchase.currency)}/{getBillingPeriod(purchase)}</Text>
            </View>
          )}
        </View>

        {isCanceled && (
          <View style={styles.expirationWarning}>
            <Icon name="exclamation-triangle" size={14} color="#92400e" solid />
            <Text size="s" style={{ color: '#92400e', flex: 1 }}>
              Your subscription has been canceled. You will have access until {nextBillDate}.
            </Text>
          </View>
        )}

        {/* Extend / Upgrade / Re-subscribe button for active subscribers */}
        {isActive && !isIapSubscriber && (
          <View style={{ marginTop: 16, gap: 8 }}>
            <Button
              variant={isCanceled ? "plus" : "secondary"}
              text={isCanceled ? t('settings.subscription.resubscribe') : t('settings.subscription.extendOrUpgrade')}
              onPress={() => openModal('SubscriptionExtendModal')}
              prependIcon={isCanceled ? "bolt" : "rotate-right"}
              isFullWidth
            />
            {/* Cancel button lives directly under Extend so subscribers can
                find it without scrolling to the bottom of the page. */}
            {hasCancelableSubscription && (
              <Button
                variant="danger"
                text={t('settings.subscription.cancelSubscription')}
                isLoading={isLoading}
                onPress={handleCancelSubscription}
                isFullWidth
              />
            )}
          </View>
        )}
        {isActive && isIapSubscriber && (
          <View style={{ marginTop: 16, gap: 8 }}>
            <Button
              variant="secondary"
              text={Platform.OS === 'web'
                ? `Manage / cancel in ${isGoogleIap ? 'Google Play' : 'App Store'}`
                : (purchase?.processor === PROCESSOR.GOOGLE ? t('settings.subscription.manageInGooglePlay') : t('settings.subscription.manageInAppStore'))}
              onPress={handleManageStoreSubscription}
              prependIcon="arrow-up-right-from-square"
              isFullWidth
            />
            {showRestorePurchases && (
              <Button
                variant="secondary"
                text={isRestoringPurchases ? t('settingsMobile.restoringPurchases') : t('settings.subscription.restorePurchases')}
                onPress={restorePurchases}
                isLoading={isRestoringPurchases}
                prependIcon="rotate-right"
                isFullWidth
              />
            )}
          </View>
        )}
        {isActive && isIapSubscriber && Platform.OS === 'web' && (
          <View style={[styles.iapWebNotice, { marginTop: 16 }]}>
            <Icon name="mobile-screen-button" size={16} color={colors.text_color_secondary} solid />
            <Text size="s" color="secondary" style={{ flex: 1 }}>
              This subscription is managed through {isGoogleIap ? 'Google Play' : 'the App Store'}. Use the button above, sign in with the store account used for your purchase, and select Gab AI to manage or cancel your subscription.
            </Text>
          </View>
        )}
      </View>
    );
  };

  // Render billing issue banner when Apple reports a billing failure
  // and the subscription is in a grace or billing-retry period.
  const renderBillingIssueBanner = () => {
    if (!billingIssue?.active || !hasActiveSubscription) return null;

    const graceDateStr = billingIssue.gracePeriodExpiresAt;
    const isGracePeriod = !!graceDateStr;
    const expiresLabel = graceDateStr
      ? formatDate(graceDateStr)
      : billingIssue.retryPeriodExpiresAt
        ? formatDate(billingIssue.retryPeriodExpiresAt)
        : null;

    return (
      <View style={[styles.billingIssueBanner, isDark && styles.billingIssueBannerDark]}>
        <Icon name="exclamation-triangle" size={18} color="#dc2626" solid />
        <View style={styles.billingIssueContent}>
          <Text size="n" weight="bold" style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}>
            Billing Issue
          </Text>
          <Text size="s" style={{ color: isDark ? '#fca5a5' : '#991b1b' }}>
            {isGracePeriod
              ? `There was a problem with your payment. Your subscription will remain active until ${expiresLabel || 'the grace period ends'}. Please update your payment method to avoid losing access.`
              : 'There was a problem renewing your subscription. Please update your payment method to continue your subscription.'}
          </Text>
          {isIapSubscriber && (Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos') ? (
            <View style={styles.billingIssueButton}>
              <Button
                variant="danger"
                text={purchase?.processor === PROCESSOR.GOOGLE ? 'Update Payment in Google Play' : 'Update Payment in App Store'}
                size="small"
                onPress={handleManageStoreSubscription}
                prependIcon="arrow-up-right-from-square"
              />
            </View>
          ) : isIapSubscriber && Platform.OS === 'web' ? (
            <Text size="s" style={{ color: isDark ? '#fca5a5' : '#991b1b', marginTop: 8 }}>
              Open the Gab AI app on your mobile device to update your payment method.
            </Text>
          ) : isWhopSubscriber ? (
            <View style={styles.billingIssueButton}>
              <Button
                variant="danger"
                text="Update Payment Method"
                size="small"
                isLoading={isLoadingManage}
                onPress={handleManageSubscription}
                prependIcon="arrow-up-right-from-square"
              />
            </View>
          ) : (Platform.OS === 'web' || Platform.OS === 'macos') ? (
            <View style={styles.billingIssueButton}>
              <Button
                variant="danger"
                text="Update Payment Method"
                size="small"
                isLoading={isUpdatingPayment}
                onPress={handleUpdatePaymentMethod}
              />
            </View>
          ) : null}
        </View>
      </View>
    );
  };

  // Render plan features section (only for active subscriptions)
  const renderPlanFeatures = () => {
    if (!hasActiveSubscription) return null;

    return (
      <View style={styles.section}>
        <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.planFeatures')}</Text>
        <View style={styles.planFeatures}>
          {buildFeatures(isPlusPremium).map((feature, index) => (
            <View key={`feature-${index}`} style={styles.feature}>
              <View style={styles.featureIcon}>
                <Icon name={feature.icon} size={14} solid color={colors.text_color_primary} />
              </View>
              <View style={styles.featureText}>
                <Text size='n' weight='bold'>{feature.title}</Text>
                {Array.isArray(feature.description) ? (
                  feature.description.map((part, i) => (
                    typeof part === 'object' ? (
                      <Text key={`feature-${index}-${i}`} size='n' weight='bold'>{part.text}</Text>
                    ) : (
                      <Text key={`feature-${index}-${i}`} size='n'>{part}</Text>
                    )
                  ))
                ) : (
                  <Text size='n'>{feature.description}</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  // Saved cards can be managed independently of subscription access.
  const renderPaymentMethod = () => {
    if (!hasValmarPaymentMethod && (!hasActiveSubscription || (!isRecurring && !isValmarSubscriber))) return null;
    
    // IAP subscribers: payment methods are managed through the App Store or Google Play
    if (isIapSubscriber) {
      const isOnIos = Platform.OS === 'ios' || Platform.OS === 'macos';
      const isOnAndroid = Platform.OS === 'android';
      const iapStoreName = isGoogleIap ? 'Google Play' : 'App Store';
      let storeName, storeDescription;
      if (isOnIos || isOnAndroid) {
        storeName = iapStoreName;
        storeDescription = `Update your payment method in your ${storeName} settings`;
      } else {
        storeName = iapStoreName;
        storeDescription = `Your payment method is managed through ${iapStoreName}. Use the store-management button above to open your subscription settings.`;
      }
      return (
        <View style={styles.section}>
          <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.paymentMethod')}</Text>
          <View style={styles.paymentMethod}>
            <View style={styles.paymentLeft}>
              <View style={styles.cardIcon}>
                <Icon name="mobile-screen-button" size={16} color={colors.text_color_primary} solid />
              </View>
              <View style={styles.cardDetails}>
                <Text size="n" weight="bold">
                  Managed via {storeName}
                </Text>
                <Text size="s" color="secondary">
                  {storeDescription}
                </Text>
              </View>
            </View>
          </View>
          {!isCanceled && <View style={styles.nextBill}>
            <Text size="s" color="secondary">
              Next bill on {nextBillDate} • {formatAmount(purchase.price, purchase.currency)}
            </Text>
          </View>}
        </View>
      );
    }
    
    // Whop subscribers manage payment methods through Whop billing portal
    if (isWhopSubscriber) {
      return (
        <View style={styles.section}>
          <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.paymentMethod')}</Text>
          
          <View style={styles.paymentMethod}>
            <View style={styles.paymentLeft}>
              <View style={styles.cardIcon}>
                <Icon name="credit-card" size={16} color={colors.text_color_primary} />
              </View>
              <View style={styles.cardDetails}>
                <Text size="n" weight="bold">
                  Managed via Whop
                </Text>
                <Text size="s" color="secondary">
                  Update your payment method in the Whop billing portal
                </Text>
              </View>
            </View>
            <Button
              variant="secondary"
              text={t('settings.subscription.update')}
              size="small"
              isLoading={isLoadingManage}
              disabled={isLoadingManage}
              onPress={handleManageSubscription}
              appendIcon="arrow-up-right-from-square"
            />
          </View>

          {!isCanceled && <View style={styles.nextBill}>
            <Text size="s" color="secondary">
              Next bill on {nextBillDate} • {formatAmount(purchase.price, purchase.currency)}
            </Text>
          </View>}
        </View>
      );
    }
    
    if (!isValmarSubscriber && !hasValmarPaymentMethod) return null;

    const valmarCardType = purchase?.maskedCardData?.cardType || userValmarCard?.cardType;
    const valmarCardLast4 = purchase?.maskedCardData?.maskedCardNumber || userValmarCard?.maskedNumber;
    const valmarCardExpiry = purchase?.maskedCardData?.expirationDate || userValmarCard?.expirationDate;

    return (
      <View style={styles.section}>
        <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.paymentMethod')}</Text>
        
        <View style={styles.paymentMethod}>
          <View style={styles.paymentLeft}>
            <View style={styles.cardIcon}>
              <Icon name="credit-card" size={16} color={colors.text_color_primary} />
            </View>
            <View style={styles.cardDetails}>
              <Text size="n" weight="bold">
                {valmarCardType || purchase?.paymentType || 'Card'} ending in {valmarCardLast4 || '****'}
              </Text>
              <Text size="s" color="secondary">
                Expiry {valmarCardExpiry || 'N/A'}
              </Text>
            </View>
          </View>
          <Button
            variant="secondary"
              text={t('settings.subscription.update')}
              size="small"
              isLoading={isUpdatingPayment}
              disabled={isUpdatingPayment}
            onPress={handleUpdatePaymentMethod}
          />
        </View>

        {isRecurring && <View style={styles.nextBill}>
          <Text size="s" color="secondary">
            Next bill on {nextBillDate} • {formatAmount(purchase.price, purchase.currency)}
          </Text>
        </View>}
      </View>
    );
  };

  // Render cancel/manage subscription section
  const renderCancelSection = () => {
    if (!hasCancelableSubscription) return null;

    // IAP subscribers: subscriptions purchased through Apple/Google must be managed via the native store
    if (isIapSubscriber) {
      const isOnNative = Platform.OS === 'ios' || Platform.OS === 'android' || Platform.OS === 'macos';
      const iapStoreName = isGoogleIap ? 'Google Play' : 'App Store';
      const nativeStoreName = purchase?.processor === PROCESSOR.GOOGLE ? 'Google Play' : 'App Store';
      let manageText, buttonText;
      if (isOnNative) {
        manageText = `Your subscription is managed through ${nativeStoreName}. To cancel, change your plan, or update your payment method, tap the button below to open your ${nativeStoreName} subscription settings.`;
        buttonText = `Manage in ${nativeStoreName}`;
      } else {
        manageText = `Open ${iapStoreName} using the button below, sign in with the store account used for your purchase, and select Gab AI to manage or cancel your subscription.`;
        buttonText = `Manage / cancel in ${iapStoreName}`;
      }
      return (
        <View style={styles.section}>
          <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.manageSubscription')}</Text>
          <Text size="s" color="secondary" style={{ marginBottom: 12 }}>
            {manageText}
          </Text>
          <View style={styles.dangerZone}>
            <Button
              variant="secondary"
              text={buttonText}
              onPress={handleManageStoreSubscription}
              prependIcon="arrow-up-right-from-square"
            />
          </View>
        </View>
      );
    }

    // Whop subscribers manage subscription via Whop billing portal.
    // The cancel button lives in the subscription status card up top.
    if (isWhopSubscriber) {
      return (
        <View style={styles.section}>
          <Text size="n" weight="bold" style={styles.sectionTitle}>{t('settings.subscription.manageSubscription')}</Text>
          <Text size="s" color="secondary" style={{ marginBottom: 12 }}>
            Update your payment method or view invoices through the Whop billing portal.
          </Text>
          <View style={styles.dangerZone}>
            <Button
              variant="primary"
              text={t('settings.subscription.manageSubscription')}
              isLoading={isLoadingManage}
              onPress={handleManageSubscription}
              prependIcon="arrow-up-right-from-square"
            />
          </View>
        </View>
      );
    }

    // US users: the cancel button now lives in the subscription status
    // card at the top of the page, so no separate section is needed here.
    return null;
  };

  // Render payment history section (always visible)
  const renderPaymentHistory = () => {
    return (
      <View style={styles.historySection}>
        <Pressable
          onPress={() => setShowHistory(!showHistory)}
          style={({ hovered }) => [
            styles.historyHeader,
            hovered && { backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }
          ]}
        >
          <View style={styles.historyHeaderLeft}>
            <Icon name="receipt" size={14} color={colors.text_color_secondary} />
            <Text size="s" weight="semibold" color="secondary" style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('settings.subscription.paymentHistory')}
            </Text>
            {historyPagination?.total > 0 && (
              <View style={styles.historyBadge}>
                <Text size="xxs" color="secondary">{historyPagination.total}</Text>
              </View>
            )}
          </View>
          <Icon 
            name={showHistory ? 'chevron-up' : 'chevron-down'} 
            size={12} 
            color={colors.text_color_tertiary} 
          />
        </Pressable>

        {showHistory && (
          <View style={{ borderTopWidth: 1, borderTopColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
            {isLoadingHistory ? (
              <View style={styles.emptyHistory}>
                <ActivityIndicator size="large" color={colors.text_color_secondary} />
                <Text size="s" color="secondary" style={{ marginTop: 12 }}>{t('settings.subscription.loadingPaymentHistory')}</Text>
              </View>
            ) : purchaseHistory.length === 0 ? (
              <View style={styles.emptyHistory}>
                <Icon name="receipt" size={24} color={colors.text_color_tertiary} />
                <Text size="s" color="secondary" style={{ marginTop: 12 }}>{t('settings.subscription.noPaymentHistory')}</Text>
                <Text size="xs" color="tertiary" style={{ marginTop: 4 }}>{t('settings.subscription.purchasesAppearHere')}</Text>
              </View>
            ) : (
              <BasicTable columns={tableColumns} noRadius={true}>
                {purchaseHistory.map((row, index) => (
                  <BasicTableRow
                    isSlim
                    key={`purchase-${index}-${row.id}`}
                    data={row}
                    renderColumn={renderHistoryColumn}
                    onHoverIn={() => setHoveredRowIndex(index)}
                    onHoverOut={() => setHoveredRowIndex(null)}
                    isHovered={hoveredRowIndex === index}
                  />
                ))}
                {historyPagination?.hasMore && (
                  <BasicTableRowLoadMoreButton
                    onPress={handleLoadMoreHistory}
                    isLoading={isLoadingMoreHistory}
                  />
                )}
              </BasicTable>
            )}
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* Section A: Subscription Status */}
      {renderSubscriptionStatus()}

      {/* Billing issue banner (grace period / billing retry) */}
      {renderBillingIssueBanner()}

      {/* Section B: Plan Features (active only) */}
      {renderPlanFeatures()}

      {/* Section C: Payment Method */}
      {renderPaymentMethod()}

      {/* Section C.5: Auto Top-Up Settings (web and macOS)
          Hidden on iOS for ALL users because auto top-up charges a card outside of Apple IAP,
          which violates App Store guidelines — even for Valmar/Whop subscribers using the iOS app. */}
      {(Platform.OS === 'web' || Platform.OS === 'macos') && <AutoTopUpSettings />}

      {/* Section D: Cancel Subscription (active recurring only) */}
      {renderCancelSection()}

      {/* Section E: Redeem Offer Code / Restore Purchases (native IAP) */}
      {(Platform.OS === 'ios' || showRestorePurchases) && (
        <View style={{ gap: 8 }}>
          {Platform.OS === 'ios' && (
            <Pressable
              onPress={handleRedeemCode}
              disabled={isRedeemingCode}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: pressed ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'transparent',
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                opacity: isRedeemingCode ? 0.6 : 1,
              })}
            >
              <Icon name="ticket" size={14} color={colors.text_color_secondary} />
              <Text size="s" color="secondary" weight="medium">
                {isRedeemingCode ? t('settings.subscription.opening') : t('settings.subscription.redeemOfferCode')}
              </Text>
            </Pressable>
          )}
          {showRestorePurchases && (
            <Pressable
              onPress={restorePurchases}
              disabled={isRestoringPurchases}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: pressed ? (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') : 'transparent',
                borderWidth: 1,
                borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                opacity: isRestoringPurchases ? 0.6 : 1,
              })}
            >
              <Icon name="rotate-right" size={14} color={colors.text_color_secondary} />
              <Text size="s" color="secondary" weight="medium">
                {isRestoringPurchases ? t('settingsMobile.restoringPurchases') : t('settings.subscription.restorePurchases')}
              </Text>
            </Pressable>
          )}
        </View>
      )}

      {/* Section F: Payment History (always visible) */}
      {renderPaymentHistory()}
    </View>
  );
}
